import React from "react";
import { useNavigate } from "react-router-dom";
import { MQTT_PREFIX } from "../../../services/mqtt/mqttconfig";
import { relativeTimestamp } from "../../../components/common/Tables/RelativeTimestamp";
import { ChevronRightIcon, ExternalLinkIcon, PencilIcon } from "../../../components/common/Icons/Icons";
import { formatTemp } from "../../../utils/temperature";

interface SpaCardProps {
  spa: any;
  unit: 'F' | 'C';
  onEdit: (spa: any) => void;
}

const tierMapDisplay: { [key: string]: string } = {
  'P': 'Prestige',
  'S': 'Signature',
  'L': 'Legend',
  'LS': 'Legend Select'
};

// The spa list carries the water temperature as a Fahrenheit string ("71"), like the mobile app reads it.
// Blank must stay unknown: Number('') is 0, which would show a very cold tub.
const waterTempF = (spa: any): number | null => {
  const raw = spa?.STemp;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

// 'on' | 'off' | null when the list doesn't say
const econState = (spa: any): 'on' | 'off' | null => {
  const v = spa?.econ;
  if (v === undefined || v === null || v === '') return null;
  const on = typeof v === 'string' ? ['1', 'true', 'on', 'yes'].includes(v.trim().toLowerCase()) : Boolean(v);
  return on ? 'on' : 'off';
};

const SpaCard: React.FC<SpaCardProps> = ({ spa, unit, onEdit }) => {
    const navigate = useNavigate();

    const isBeta = MQTT_PREFIX.includes('beta');

    const isColdTub = (() => {
      const brand = localStorage.getItem('data-brand');
      if (brand) return brand === 'coldtub';
      return window.location.hostname.includes('mycoldtub');
    })();

    const redirectDomain = isColdTub ? 'mycoldtub.com' : 'myarcticspa.com';

    const redirectSiteUrl = isBeta
      ? `https://beta.${redirectDomain}/Spa/MySpa.aspx?ignore_redirect=true`
      : `https://${redirectDomain}/Spa/MySpa.aspx?ignore_redirect=true`;

    const isV1 = !spa?.is_moved;
    const isOnline = Boolean(spa?.is_connected);
    const tempF = waterTempF(spa);
    const econ = econState(spa);
    const name = spa?.nick_name || 'Unnamed spa';
    const model = spa?.tub_model
      ? `${spa.tub_model.name} ${tierMapDisplay[spa.tub_model.tier] ?? ''}${spa.tub_model.sds ? ' + SDS' : ''}`.trim()
      : '';

    const handleOpen = () => {
      if (spa?.is_moved) {
        navigate(`/spa-control/${spa.id}`);
      } else {
        // Redirect to classic site for v1 spas in a new tab
        window.open(redirectSiteUrl, '_blank', 'noopener,noreferrer');
      }
    }

  return (
    <article className={`card ms-card ${isOnline ? "online" : "offline"}${isV1 ? " v1" : ""}`}>
      <div className="ms-dial" aria-label={tempF !== null ? `Water ${formatTemp(tempF, unit)}°${unit}` : 'Water temperature unknown'}>
        {tempF !== null
          ? <><span className="ms-dial-value">{formatTemp(tempF, unit)}</span><span className="ms-dial-unit">°{unit}</span></>
          : <span className="ms-dial-value muted">--</span>}
      </div>

      <div className="ms-card-content">
        <div className="ms-card-title-row">
          <h2 className="ms-card-title">
            {/* Stretched over the whole card, so anywhere opens the spa */}
            <button type="button" className="ms-card-open" onClick={handleOpen}>{name}</button>
          </h2>
          <button
            type="button"
            className="btn btn-ghost icon-btn ms-card-edit"
            onClick={() => onEdit(spa)}
            aria-label={`Rename ${name}`}
            title="Rename"
          >
            <PencilIcon/>
          </button>
        </div>

        {isV1 &&
          <p className="ms-v1-note">
            <span className="ms-v1-tag">V1</span>
          </p>}

        {model && <p className="ms-card-model">{model}</p>}

        <ul className="ms-status-rows">
          <li className={isOnline ? 'online' : 'muted'}>
            {isOnline ? 'Online' : 'Offline'}
            {!isOnline && spa?.last_connected && <span className="ms-status-when"> · {relativeTimestamp(undefined, spa.last_connected)}</span>}
          </li>
          {econ && <li className={econ === 'on' ? 'online' : 'alert'}>Econ {econ}</li>}
        </ul>
      </div>

      <span className="ms-card-action" aria-hidden="true">
        {isV1 ? <>Open classic site <ExternalLinkIcon/></> : <>Control now <ChevronRightIcon/></>}
      </span>
    </article>
  );
};

export default SpaCard;
